import type { BundleData, BundleVariant } from "@/lib/home/bundle-products";
import { resolveMerchandiseSubtotal } from "@/lib/cart/merchandise-subtotal";

export type Qty = 1 | 2 | 3;

const RETA_SKU_BY_QTY: Record<Qty, string> = {
	1: "RETA-10MG-1VIAL",
	2: "RETA-10MG-1VIAL-2PK",
	3: "RETA-10MG-1VIAL-3PK",
};

const BAC_SKU_BY_QTY: Record<Qty, string> = {
	1: "BAC-10ML-1VIAL",
	2: "BAC-10ML-1VIAL-2PKG",
	3: "BAC-10ML-1VIAL-3PKG",
};

export function pickVariantForQty(variants: BundleVariant[], qty: Qty): BundleVariant | undefined {
	const isReta = variants[0]?.sku.startsWith("RETA");
	const sku = isReta ? RETA_SKU_BY_QTY[qty] : BAC_SKU_BY_QTY[qty];
	return variants.find((v) => v.sku === sku);
}

export interface Totals {
	retaTotal: number;
	bacTotal: number;
	grand: number;
	currency: string;
	savingsVsSinglePacks: number; // reta only
	nextTierSavingsPerUnit: number | null;
	isMaxTier: boolean;
	selectionBasis: number;
	priceReady: boolean;
}

function resolveSelectedPrice(variant: BundleVariant | null | undefined) {
	const price = variant?.pricing?.price;
	const amount = price?.gross.amount;
	const currency = price?.gross.currency;
	const basis = resolveMerchandiseSubtotal(price, Number.NaN);
	if (
		typeof amount !== "number" ||
		!Number.isFinite(amount) ||
		amount < 0 ||
		typeof currency !== "string" ||
		currency.length === 0 ||
		!Number.isFinite(basis) ||
		basis < 0
	) {
		return null;
	}
	return { basis, currency };
}

export function computeTotals(bundle: BundleData, qty: Qty, addWater: boolean): Totals {
	const reta = pickVariantForQty(bundle.reta, qty);
	const bac = addWater ? pickVariantForQty(bundle.bac, 1) : null;
	const retaTotal = reta?.pricing?.price?.gross.amount ?? 0;
	const bacTotal = bac?.pricing?.price?.gross.amount ?? 0;
	const currency = reta?.pricing?.price?.gross.currency ?? "EUR";
	const singlePackUnit = bundle.reta[0]?.pricing?.price?.gross.amount ?? 0;
	const savingsVsSinglePacks = Math.max(0, singlePackUnit * qty - retaTotal);
	const retaPrice = resolveSelectedPrice(reta);
	const bacPrice = addWater ? resolveSelectedPrice(bac) : null;
	const priceReady = Boolean(
		retaPrice && (!addWater || (bacPrice && bacPrice.currency === retaPrice.currency)),
	);
	const selectionBasis = priceReady && retaPrice ? retaPrice.basis + (bacPrice?.basis ?? 0) : Number.NaN;

	let nextTierSavingsPerUnit: number | null = null;
	const isMaxTier = qty === 3;
	if (!isMaxTier) {
		const nextQty = (qty + 1) as Qty;
		const nextReta = pickVariantForQty(bundle.reta, nextQty);
		if (nextReta && reta) {
			const currentPerUnit = retaTotal / qty;
			const nextPerUnit = (nextReta.pricing?.price?.gross.amount ?? 0) / nextQty;
			nextTierSavingsPerUnit = Math.max(0, currentPerUnit - nextPerUnit);
		}
	}

	return {
		retaTotal,
		bacTotal,
		grand: retaTotal + bacTotal,
		currency,
		savingsVsSinglePacks,
		nextTierSavingsPerUnit,
		isMaxTier,
		selectionBasis,
		priceReady,
	};
}
